import type { CSSProperties } from 'react';

export interface RingProps {
  value?: number;
  size?: number;
  stroke?: number;
  color?: string;
  track?: string;
  /** label text; pass `false` to hide the centred number */
  label?: string | false;
}

/** SVG progress ring (a circle — the one allowed hand-drawn shape). */
export function Ring({
  value = 0,
  size = 44,
  stroke = 4,
  color = 'var(--sage)',
  track = 'var(--paper-2)',
  label,
}: RingProps) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const off = c * (1 - value / 100);
  const labelStyle: CSSProperties = {
    position: 'absolute',
    inset: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: size * 0.26,
    fontWeight: 600,
    fontFamily: 'var(--mono)',
    color: 'var(--ink)',
  };
  return (
    <div style={{ position: 'relative', width: size, height: size, flex: 'none' }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={track} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={c}
          strokeDashoffset={off}
          strokeLinecap="round"
          style={{ transition: 'stroke-dashoffset .6s cubic-bezier(.2,.7,.2,1)' }}
        />
      </svg>
      {label !== false && <div style={labelStyle}>{label ?? `${value}`}</div>}
    </div>
  );
}

/** Thin progress bar. */
export function Bar({ value = 0, style }: { value?: number; style?: CSSProperties }) {
  return (
    <div className="track" style={style}>
      <i style={{ width: `${Math.max(0, Math.min(100, value))}%` }} />
    </div>
  );
}
